<template>
  <mf-loading-dialog :loading="isLoadingSms">
    <v-dialog value="true" class="d-flex" persistent width="550px" scrollable>
      <v-card class="dialog-content pa-8 d-flex flex-column align-center" width="550px">
        <h3 class="title pl-4 mb-6 align-self-start">{{ cardTitle }}</h3>
        <v-container v-if="!isFromRaffler" :class="{ ...winnerBoxStyle }">
          <v-col v-if="previousWinner" class="pa-0 mb-8">
            <p class="search-bar-top-and-bottom-text">Ganhador atual:</p>
            <p>Nome: {{ previousWinner.user.name }}</p>
            <p>CPF/CNPJ: {{ previousWinner.user.document_no | cpfCnpj }}</p>
            <p>Data de geração do número da sorte: {{ previousWinner.lucky_number.created_at | date({ fmtIn: 'yyyy-mm-dd', fmtOut: 'dd/mm/yyyy' }) }}</p>
            <p>Data da venda do número da sorte: {{ previousWinner.lucky_number.coupon_created_at | date({ fmtIn: 'yyyy-mm-dd', fmtOut: 'dd/mm/yyyy' }) }}</p>
            <p>Loja adquirida: {{ previousWinner.lucky_number.unit.name }}</p>
          </v-col>
          <p class="search-bar-top-and-bottom-text">{{ searchBarTopText }}</p>
          <mf-search
            v-model.lazy="luckyNumberSearch"
            class="text-field--full-row"
            hide-details
            label="Número da sorte"
            placeholder="1234565"
            type="number"
            @keydown="preventDefault($event, 'keydown')"
            @wheel="preventDefault($event, 'wheel')"
          />
          <p class="search-bar-top-and-bottom-text">Ganhador:</p>
          <v-progress-circular v-if="isLoading" indeterminate />
          <p v-if="!isLoading && showNoParticipantFoundMessage">Não foi encontrado um CPF ou CNPJ vinculado a esse número.</p>
          <v-col v-if="newWinner && !isLoading" class="pa-0">
            <p>Nome: {{ newWinner.user.name }}</p>
            <p>CPF/CNPJ: {{ newWinner.user.document_no | cpfCnpj }}</p>
            <p>Data de geração do número da sorte: {{ newWinner.lucky_number.created_at | date({ fmtIn: 'yyyy-mm-dd', fmtOut: 'dd/mm/yyyy' }) }}</p>
            <p>Data da venda do número da sorte: {{ newWinner.lucky_number.coupon_created_at | date({ fmtIn: 'yyyy-mm-dd', fmtOut: 'dd/mm/yyyy' }) }}</p>
            <p>Loja adquirida: {{ newWinner.lucky_number.unit.name }}</p>
          </v-col>
        </v-container>
        <v-container class="pa-4 gray-box gray-box--message">
          <h4 class="mb-0">Canais para anunciar o ganhador</h4>
          <v-row class="d-flex align-center justify-start pa-2">
            <v-checkbox
              v-model="notificationChannels['push']"
              class="mt-0"
              :disabled="!notificationChannels['sms'] || smsCredits === 0"
              hide-details
              label="APP"
            />
            <v-checkbox
              v-model="notificationChannels['sms']"
              class="mt-0 mr-1"
              :disabled="!notificationChannels['push'] || smsCredits === 0"
              hide-details
              label="SMS"
            />
            <v-tooltip v-if="smsCredits === 0" max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <span v-on="on">
                    <mf-icon class="help-icon" size="18">info</mf-icon>
                  </span>
                </span>
              </template>
              <span>Você não possui créditos de SMS ou o módulo de SMS ativo.</span>
            </v-tooltip>
          </v-row>
          <v-textarea
            v-model="notificationMessage"
            class="mt-4"
            label="Customizar a mensagem SMS"
            :error-messages="notificationMessageError"
            outlined
            no-resize
            rows="5"
            counter="160"
          />
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: previousWinner ? 'Editar' : 'Salvar',
            action: validateAndSave,
            isVisible: true,
            isDisabled,
          }"
          :extra-button="{
            text: 'Cancelar',
            action: () => $emit('close'),
            isVisible: true,
          }"
        />
      </v-card>
    </v-dialog>
  </mf-loading-dialog>
</template>

<script>
import { QUERY_RAFFLE_PARTICIPANTS } from '../../graphql'
import { QUERY_GET_CREDITS } from '@/modules/campanhas/campanhas-sms/graphql/index.js'
import { preventNumberEvents } from '../../helpers'
import { maxLength, required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { mapGetters } from 'vuex'

export default {
  name: 'EditWinnerDialog',
  props: {
    newWinner: {
      type: Object,
      required: false,
      default: () => {},
    },
    previousWinner: {
      type: Object,
      required: false,
      default: () => {},
    },
    setNewWinner: {
      type: Function,
      required: false,
    },
    isFromRaffler: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    luckyNumberSearch: '',
    isLoadingSms: true,
    isLoading: false,
    showNoParticipantFoundMessage: false,
    smsCredits: 0,
    notificationChannels: {
      sms: true,
      push: true,
    },
    notificationMessage: '',
    originalMessage: '',
    timeout: null,
    isSaving: false,
  }),
  computed: {
    cardTitle() {
      if (!this.previousWinner) return 'Inserir ganhador'
      return 'Editar ganhador'
    },
    winnerBoxStyle() {
      return {
        'pa-4': true,
        'mb-2': true,
        'gray-box': true,
        'gray-box--edit-winner': this.previousWinner,
        'gray-box--new-winner': !this.previousWinner,
      }
    },
    searchBarTopText() {
      return this.previousWinner ? 'Insira o número da sorte que irá substituir o atual' : 'Insira o número da sorte que deseja adicionar'
    },
    notificationMessageError() {
      if (!this.$v.notificationMessage) return ''
      if (this.$v.notificationMessage.$error) {
        if (!this.$v.notificationMessage.required) return 'Informe um texto de anúncio.'
        else if (!this.$v.notificationMessage.maxLength) return 'O texto da mensagem excedeu 160 caracteres.'
        else if (!this.$v.notificationMessage.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    isDisabled() {
      if (!this.isFromRaffler) {
        return !this.newWinner
      } else {
        return this.isSaving
      }
    },
  },
  watch: {
    async luckyNumberSearch() {
      if (!this.luckyNumberSearch) {
        this.setNewWinner(null)
        this.showNoParticipantFoundMessage = false
        this.isLoading = false
        return
      }
      await this.getData()
    },
  },
  mounted() {
    this.getSmsCredits()
    if (this.previousWinner) {
      this.notificationMessage = this.previousWinner.notifications.message
    } else {
      const loggedUser = this.$getLoggedUser()
      this.notificationMessage = `Você foi sorteado na promoção do mercado ${loggedUser.client}. Entre em contato com o mercado para retirar o prêmio.`
    }
  },
  methods: {
    ...mapGetters(['generateWinner']),
    preventDefault(event, type) {
      return preventNumberEvents(event, type)
    },
    async getSmsCredits() {
      try {
        const result = await this.$apollo.query({
          query: QUERY_GET_CREDITS,
          fetchPolicy: 'no-cache',
        })
        this.smsCredits = result.data.balance
        if (this.smsCredits === 0) this.notificationChannels.sms = false
        this.isLoadingSms = false
      } catch (e) {
        this.$snackbar({ message: 'Ocorreu um erro ao consultar os créditos disponíveis.', snackbarColor: 'error' })
        this.smsCredits = 0
        this.isLoadingSms = false
        console.log(e)
      }
    },
    async getData() {
      this.isLoading = true
      this.showNoParticipantFoundMessage = false
      this.setNewWinner(null)
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.$apollo
          .query({
            query: QUERY_RAFFLE_PARTICIPANTS,
            variables: {
              raffle_id: this.$route.params.id,
              search: this.luckyNumberSearch,
            },
            fetchPolicy: 'no-cache',
          })
          .then((response) => {
            if (response.data.raffleParticipants.raffleParticipants.length > 0) {
              this.showNoParticipantFoundMessage = false
              const formattedWinner = {
                ...response.data.raffleParticipants.raffleParticipants[0],
              }
              formattedWinner.notification_channels = []
              Object.keys(this.notificationChannels).map((key) => {
                if (this.notificationChannels[key]) {
                  formattedWinner.notification_channels.push(key.toUpperCase())
                }
              })
              formattedWinner.notification_message = this.notificationMessage
              formattedWinner.prize_given = false
              this.setNewWinner(formattedWinner)
            } else if (response.data.raffleParticipants.raffleParticipants.length === 0) {
              this.setNewWinner(null)
              this.showNoParticipantFoundMessage = true
            }
            this.isLoading = false
          })
          .catch((err) => {
            console.log(err)
            this.$snackbar({ message: 'Erro ao procurar por número da sorte', snackbarColor: 'error' })
            this.isLoading = false
          })
      }, 500)
    },
    validateAndSave() {
      this.isSaving = true
      this.$v.$reset()
      this.$v.$touch()
      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
      } else {
        const notifications = {
          message: this.notificationMessage,
          channels: [],
        }
        Object.keys(this.notificationChannels).map((key) => {
          if (this.notificationChannels[key]) {
            notifications.channels.push(key.toUpperCase())
          }
        })
        if (this.isFromRaffler) {
          // TODO SAVE WINNERS
          this.$emit('save', notifications)
        } else {
          const formattedWinner = {
            ...this.newWinner,
            notifications,
          }
          formattedWinner.prize_given = false
          this.setNewWinner(formattedWinner)
          this.$emit('save')
        }
      }
      this.isSaving = false
    },
  },
  validations() {
    let validations = {
      notificationMessage: {
        required,
        maxLength: maxLength(160),
        notOnlySpace,
      },
    }
    return validations
  },
}
</script>

<style lang="scss" scoped>
.dialog-content {
  height: fit-content;
}
.search-bar-top-and-bottom-text {
  margin-bottom: 10px;
  margin-top: 10px;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  text-align: left;
}
p {
  margin-bottom: 5px;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0em;
  text-align: left;
}
h4 {
  font-weight: 800;
}
::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.gray-box {
  background: $cinza-l90;
  border-radius: 8px;
  width: 450px;
  &--new-winner {
    height: 300px;
  }
  &--edit-winner {
    height: 450px;
  }
  &--message {
    height: 300px;
  }

  .v-input--checkbox {
    margin-right: 100px;
  }
}
</style>
